import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { Trash2, LogOut, Plus, Upload, Download, Copy, Check, Search, Pencil, X, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "wedding_admin_token";
const MONOGRAM_SAGE = "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/itxixsdb_Monogram_526E3B.png";

const emptyForm = { first_name: "", last_name: "", email: "", invitation_code: "", reserved_seats: 1, language: "es", notes: "" };

const Admin = () => {
  const { t, lang, setLang } = useLanguage();
  const [token, setToken] = useState(() => window.localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { "X-Admin-Token": token };

  const loadData = async () => {
    setLoading(true);
    try {
      const [g, s] = await Promise.all([
        axios.get(`${API}/admin/guests`, { headers }),
        axios.get(`${API}/admin/guests/stats`, { headers }),
      ]);
      setGuests(g.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken("");
        window.localStorage.removeItem(TOKEN_KEY);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      window.localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch {
      setError(t.admin.invalidPassword);
    }
  };

  const logout = () => {
    window.localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setGuests([]);
    setStats(null);
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowAdd(true);
  };

  const openEdit = (g) => {
    setForm({
      first_name: g.first_name || "",
      last_name: g.last_name || "",
      email: g.email || "",
      phone: g.phone || "",
      invitation_code: g.invitation_code || "",
      reserved_seats: g.reserved_seats || 1,
      language: g.language || "es",
      notes: g.notes || "",
    });
    setEditingId(g.id);
    setShowAdd(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/guests/${editingId}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/guests`, form, { headers });
      }
      setShowAdd(false);
      loadData();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this guest?")) return;
    await axios.delete(`${API}/admin/guests/${id}`, { headers });
    loadData();
  };

  const handleImport = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/guests/bulk`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      loadData();
    } catch {
      setImportResult({ error: t.admin.guests.importModal.error });
    }
  };

  const downloadTemplate = () => {
    // BOM for Excel UTF-8 compatibility (respects accents/ñ)
    const bom = "\uFEFF";
    const header = "nombre,apellido,telefono,correo,lugares,idioma,codigo,notas";
    const rows = [
      "María,García López,+5213312345678,maria@correo.com,2,es,,Prima de la novia",
      "Juan Carlos,Ramírez,+5213398765432,juan@correo.com,2,es,JUAN-Y-ANA,Amigo de Edouard",
      "Sophie,Dubois,+32470123456,sophie@mail.be,2,en,,Amiga de París",
      "Familia,Alvarez,+5213311122233,,4,es,,Familia — mesa 5",
    ];
    const csv = bom + header + "\n" + rows.join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla-invitados.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const { data } = await axios.get(`${API}/admin/guests/export.csv`, { headers, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "guests.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyLink = async (g) => {
    const url = `${window.location.origin}/rsvp?guest=${encodeURIComponent(g.invitation_code)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedId(g.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const sendWhatsapp = (row) => {
    const url = `${window.location.origin}/rsvp?guest=${encodeURIComponent(row.invitation_code)}`;
    const guestLang = row.language === "en" ? "en" : "es";
    const template = translations[guestLang].admin.guests.whatsappMessage;
    const name = `${row.first_name}${row.last_name ? " " + row.last_name : ""}`.trim();
    const msg = template.replace("{name}", name).replace("{link}", url);
    const phoneClean = (row.phone || "").replace(/[^\d]/g, "");
    const base = phoneClean ? `https://wa.me/${phoneClean}` : `https://wa.me/`;
    const waUrl = `${base}?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return guests.filter((g) => {
      if (filter === "pending" && g.rsvp_status !== "pending") return false;
      if (filter === "confirmed" && g.rsvp_status !== "confirmed") return false;
      if (filter === "declined" && g.rsvp_status !== "declined") return false;
      if (!q) return true;
      return (
        `${g.first_name} ${g.last_name}`.toLowerCase().includes(q) ||
        (g.email || "").toLowerCase().includes(q) ||
        (g.invitation_code || "").toLowerCase().includes(q)
      );
    });
  }, [guests, search, filter]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--ivory)]">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="bg-white border border-[var(--border-soft)] p-10 md:p-14 w-full max-w-md"
        >
          <div className="text-center mb-10">
            <img src={MONOGRAM_SAGE} alt="A&E" className="h-16 w-auto mx-auto mb-4 object-contain" />
            <p className="eyebrow">{t.admin.loginTitle}</p>
          </div>
          <label className="luxe-label">{t.admin.password}</label>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="luxe-input mb-6"
            autoFocus
          />
          {error && <p data-testid="admin-error" className="text-red-600 text-sm mb-4">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" className="btn-primary w-full">
            {t.admin.login}
          </button>
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="text-[11px] tracking-[0.22em] uppercase text-[var(--text-secondary)] hover:text-[var(--sage-deep)]"
            >
              {lang === "es" ? "English" : "Español"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const g = t.admin.guests;
  const statCards = [
    { label: g.stats.totalGuests, value: stats?.total_guests ?? 0 },
    { label: g.stats.confirmed, value: stats?.confirmed ?? 0, tone: "sage" },
    { label: g.stats.declined, value: stats?.declined ?? 0 },
    { label: g.stats.pending, value: stats?.pending ?? 0 },
    { label: g.stats.reservedSeats, value: stats?.reserved_seats ?? 0 },
    { label: g.stats.confirmedSeats, value: stats?.confirmed_seats ?? 0, tone: "accent" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--ivory)] px-4 md:px-8 py-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <img src={MONOGRAM_SAGE} alt="A&E" className="h-12 object-contain" />
            <div>
              <p className="eyebrow mb-1">Alejandra & Edouard · 11.28.2026</p>
              <h1 className="font-serif text-2xl md:text-3xl text-[var(--text-primary)]">{t.admin.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="text-[11px] tracking-[0.22em] uppercase text-[var(--text-secondary)] hover:text-[var(--sage-deep)]"
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase border border-[var(--border-soft)] px-4 py-2 hover:border-[var(--text-primary)]"
            >
              <LogOut className="w-3 h-3" /> {t.admin.logout}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.label} data-testid={`stat-${c.label}`} className="bg-white border border-[var(--border-soft)] p-5">
              <p className="eyebrow text-[10px] mb-3">{c.label}</p>
              <div
                className={`font-serif text-3xl md:text-4xl tabular-nums ${
                  c.tone === "sage" ? "text-[var(--sage-deep)]" : c.tone === "accent" ? "text-[var(--accent)]" : "text-[var(--text-primary)]"
                }`}
              >
                {c.value}
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-[var(--border-soft)] p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[220px] border border-[var(--border-soft)] px-3 py-2">
            <Search className="w-4 h-4 text-[var(--text-secondary)]" />
            <input
              data-testid="guests-search"
              placeholder={g.search}
              className="flex-1 bg-transparent outline-none text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="guests-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-[var(--border-soft)] px-3 py-2 text-[11px] tracking-[0.22em] uppercase bg-white"
          >
            <option value="all">{g.filterAll}</option>
            <option value="pending">{g.filterPending}</option>
            <option value="confirmed">{g.filterConfirmed}</option>
            <option value="declined">{g.filterDeclined}</option>
          </select>
          <button
            data-testid="guests-add"
            onClick={openAdd}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--sage-deep)] text-white px-4 py-2 hover:bg-[var(--text-primary)]"
          >
            <Plus className="w-3 h-3" /> {g.addGuest}
          </button>
          <button
            data-testid="guests-import"
            onClick={() => { setImportResult(null); setShowImport(true); }}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase border border-[var(--border-soft)] px-4 py-2 hover:border-[var(--text-primary)]"
          >
            <Upload className="w-3 h-3" /> {g.importCsv}
          </button>
          <button
            data-testid="guests-export"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase border border-[var(--border-soft)] px-4 py-2 hover:border-[var(--text-primary)]"
          >
            <Download className="w-3 h-3" /> {g.export}
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-[var(--border-soft)] overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[var(--text-secondary)]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div data-testid="guests-empty" className="p-16 text-center text-[var(--text-secondary)]">
              {g.empty}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="guests-table" className="w-full text-sm">
                <thead className="bg-[var(--ivory)] text-[var(--text-secondary)]">
                  <tr className="text-left text-[10px] tracking-[0.22em] uppercase">
                    <th className="px-4 py-4">{g.cols.name}</th>
                    <th className="px-4 py-4">{g.cols.code}</th>
                    <th className="px-4 py-4">{g.cols.reserved}</th>
                    <th className="px-4 py-4">{g.cols.confirmed}</th>
                    <th className="px-4 py-4">{g.cols.status}</th>
                    <th className="px-4 py-4">{g.cols.phone}</th>
                    <th className="px-4 py-4">{g.cols.email}</th>
                    <th className="px-4 py-4">{g.cols.dietary}</th>
                    <th className="px-4 py-4">{g.cols.responseDate}</th>
                    <th className="px-4 py-4">{g.cols.link}</th>
                    <th className="px-4 py-4 text-right">{g.cols.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => {
                    const statusColors = {
                      pending: "bg-[var(--secondary)] text-[var(--text-secondary)]",
                      confirmed: "bg-[var(--sage-soft)] text-[var(--sage-deep)]",
                      declined: "bg-red-50 text-red-700",
                    };
                    return (
                      <tr key={row.id} className="border-t border-[var(--border-soft)] hover:bg-[var(--ivory-50)]">
                        <td className="px-4 py-3 font-serif text-base text-[var(--text-primary)] whitespace-nowrap">
                          {row.first_name} {row.last_name}
                          {row.notes && (
                            <div className="text-[10px] text-[var(--text-secondary)] italic font-sans-luxe normal-case">{row.notes}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--text-primary)]">{row.invitation_code}</td>
                        <td className="px-4 py-3 tabular-nums">{row.reserved_seats}</td>
                        <td className="px-4 py-3 tabular-nums">{row.confirmed_seats || 0}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 ${statusColors[row.rsvp_status]}`}>
                            {g.status[row.rsvp_status] || row.rsvp_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">{row.phone || "—"}</td>
                        <td className="px-4 py-3 text-[var(--text-secondary)]">{row.email || "—"}</td>
                        <td className="px-4 py-3 text-[var(--text-secondary)] max-w-[180px] truncate">{row.dietary_requirements || "—"}</td>
                        <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">
                          {row.response_date
                            ? new Date(row.response_date).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { day: "2-digit", month: "short", year: "numeric" })
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1.5">
                            <button
                              data-testid={`copy-${row.invitation_code}`}
                              onClick={() => copyLink(row)}
                              className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[var(--sage-deep)] hover:text-[var(--text-primary)]"
                            >
                              {copiedId === row.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              {copiedId === row.id ? g.linkCopied : g.copyLink}
                            </button>
                            <button
                              data-testid={`whatsapp-${row.invitation_code}`}
                              onClick={() => sendWhatsapp(row)}
                              className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[#25D366] hover:text-[#128C7E]"
                              title={g.sendWhatsapp}
                            >
                              <MessageCircle className="w-3 h-3" />
                              {g.sendWhatsapp}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => openEdit(row)} className="text-[var(--text-secondary)] hover:text-[var(--sage-deep)] mr-3" title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(row.id)} className="text-[var(--text-secondary)] hover:text-red-600" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <form onSubmit={submitForm} data-testid="add-guest-form" className="bg-white border border-[var(--border-soft)] p-8 md:p-10 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">{g.addModal.title}</h2>
              <button type="button" onClick={() => setShowAdd(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="luxe-label">{g.addModal.firstName} *</label>
                <input required className="luxe-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <label className="luxe-label">{g.addModal.lastName}</label>
                <input className="luxe-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div>
                <label className="luxe-label">{g.addModal.email}</label>
                <input type="email" className="luxe-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="luxe-label">{g.addModal.phone}</label>
                <input
                  type="tel"
                  className="luxe-input"
                  placeholder={g.addModal.phonePlaceholder}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="luxe-label">{g.addModal.code}</label>
                <input className="luxe-input font-mono uppercase" value={form.invitation_code} onChange={(e) => setForm({ ...form, invitation_code: e.target.value })} placeholder="ANA2026" />
                <p className="text-[10px] text-[var(--text-secondary)] mt-2">{g.addModal.codeHelp}</p>
              </div>
              <div>
                <label className="luxe-label">{g.addModal.seats}</label>
                <input type="number" min={1} max={20} className="luxe-input" value={form.reserved_seats} onChange={(e) => setForm({ ...form, reserved_seats: parseInt(e.target.value || "1", 10) })} />
              </div>
              <div>
                <label className="luxe-label">{g.addModal.language}</label>
                <select className="luxe-input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="luxe-label">{g.addModal.notes}</label>
                <input className="luxe-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-outline">{g.addModal.cancel}</button>
              <button type="submit" className="btn-primary">{g.addModal.save}</button>
            </div>
          </form>
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white border border-[var(--border-soft)] p-8 md:p-10 w-full max-w-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">{g.importModal.title}</h2>
              <button type="button" onClick={() => setShowImport(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5">{g.importModal.help}</p>
            <button onClick={downloadTemplate} className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] hover:text-[var(--text-primary)] mb-5">
              <Download className="w-3 h-3" /> {g.importModal.downloadTemplate}
            </button>
            <input ref={fileInputRef} type="file" accept=".csv" className="block w-full text-sm mb-5 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[var(--sage-deep)] file:text-white file:text-[11px] file:tracking-[0.22em] file:uppercase" />
            {importResult && (
              <div className="text-sm mb-4">
                {importResult.error ? (
                  <p className="text-red-600">{importResult.error}</p>
                ) : (
                  <p className="text-[var(--sage-deep)]">
                    {g.importModal.imported.replace("{n}", importResult.imported)}
                    {importResult.skipped > 0 && ` · skipped ${importResult.skipped}`}
                  </p>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowImport(false)} className="btn-outline">{g.addModal.cancel}</button>
              <button onClick={handleImport} className="btn-primary">{g.importModal.import}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
